import type { AvailableInstrumentRow } from "../types/tradeManagementReport";
import { formatAvailableInstrumentOptionLabel } from "./marketDataSourceChoice";

/** True when the symbol can be used for contextualized reports (regime, trade management, etc.). */
export function isInstrumentSelectableForContextReport(row: AvailableInstrumentRow): boolean {
  return row.md_supported !== false;
}

const TECHNICAL_SELECTION_HINT =
  /md_supported|POST\s+\/api|\/instruments\/resolve|broker_account_code|before generate/i;

/** Drop backend/dev selection hints; the UI explains support via disabled options. */
export function userFacingInstrumentsSelectionHint(hint: string | null | undefined): string | null {
  const trimmed = hint?.trim();
  if (!trimmed || TECHNICAL_SELECTION_HINT.test(trimmed)) return null;
  return trimmed;
}

export function partitionInstrumentsByMarketDataSupport(instruments: AvailableInstrumentRow[]): {
  supported: AvailableInstrumentRow[];
  unsupported: AvailableInstrumentRow[];
} {
  const supported: AvailableInstrumentRow[] = [];
  const unsupported: AvailableInstrumentRow[] = [];
  for (const row of instruments) {
    if (isInstrumentSelectableForContextReport(row)) supported.push(row);
    else unsupported.push(row);
  }
  return { supported, unsupported };
}

export function buildReportInstrumentFieldDescription(input: {
  instruments: AvailableInstrumentRow[];
  instrumentsLoading: boolean;
  symbolRequired: boolean;
  backendHint?: string | null;
}): string {
  if (input.instrumentsLoading) {
    return "Loading symbols for your account and date range…";
  }

  const friendlyBackend = userFacingInstrumentsSelectionHint(input.backendHint);
  if (friendlyBackend) return friendlyBackend;

  const { supported, unsupported } = partitionInstrumentsByMarketDataSupport(input.instruments);

  if (input.instruments.length === 0) {
    return "No symbols with trades in this account and date range.";
  }

  if (unsupported.length === 0) {
    return input.symbolRequired
      ? "Choose a symbol for market context. Options show trade counts and OHLC source."
      : "Optional symbol for market context. Options show trade counts and OHLC source.";
  }

  if (supported.length === 0) {
    return "These symbols traded in this range. Confirm market data if a symbol still needs OHLC.";
  }

  const availability = `${supported.length} with OHLC · ${unsupported.length} still need market data setup`;
  return input.symbolRequired
    ? `Choose a symbol for market context. ${availability}.`
    : `Optional symbol for market context. ${availability}.`;
}

export function buildReportInstrumentSelectOptions(
  instruments: AvailableInstrumentRow[]
): { value: string; label: string; disabled?: boolean }[] {
  const sortBySymbol = (a: AvailableInstrumentRow, b: AvailableInstrumentRow) =>
    a.symbol.localeCompare(b.symbol);

  return [...instruments].sort(sortBySymbol).map((row) => ({
    value: row.symbol,
    label: formatAvailableInstrumentOptionLabel(row),
  }));
}
